import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { IAccount } from '@services/accounts.service';
import { AssetsDataService } from '@services/assets-data.service';
import { AuthenticationService } from '@services/authentication.service';
import { DataPoint, DatapointsDataService } from '@services/datapoints-data.service';
import { IntegrationsService, IWebhookIntegration } from '@services/integrations.service';
import { OrganisationDataService } from '@services/organisation-data.service';
import { Rule, RulesDataService } from '@services/rules-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { AnnotationsService, GaugeTheme, GaugeTooltipService, ILoadedEventArgs, LegendService } from '@syncfusion/ej2-angular-circulargauge';
import { combineLatest, distinctUntilChanged, filter, finalize, Subject, takeUntil } from 'rxjs';

@Component({
	selector: 'app-dashboard-health-panel',
	templateUrl: './dashboard-health-panel.component.html',
	styleUrls: ['./dashboard-health-panel.component.scss'],
	providers: [AnnotationsService, LegendService, GaugeTooltipService]
})
export class DashboardHealthPanelComponent implements AfterViewInit, OnDestroy {

	/** Observable Subject Finalise */
	private finalise = new Subject<void>();

	/** accountID */
	@Input() accountID: string;
	/** selectedAccount */
	public selectedAccount: IAccount;
	/** OrganisationID */
	@Input() public OrganisationUID: string;

	/** Datapoints */
	public Datapoints: DataPoint[] = [];

	/** Rules */
	public Rules: Rule[] = [];

	/** Webhooks */
	public Webhooks: IWebhookIntegration[] = [];

	/** HealthValue */
	public HealthValue: number = 4;

	/** fieldsDP */
	public fieldsDP: Object = { text: 'DPTitle', id: 'DataPointID' };
	/** fieldsR */
	public fieldsR: Object = { text: 'RuleDescription', id: 'RuleID' };
	/** fieldsW */
	public fieldsW: Object = { text: 'ClientWebhookID', id: 'WebhookID' };

	/** legendSettings */
	public legendSettings: Object = {
		visible: true,
		position: 'Top',
		// location: { x: 20, y: 0 },
		width: '100%'
	};

	/** labelStyle */
	public labelStyle: Object = {
		font: {
			size: '12px',
			fontFamily: 'inherit'
		},
		position: 'Outside',
		offset: -40,
	};

	/** annotations */
	public annotations: Object = [
		{
			content:
				'<div style="font-size:16px;margin-top: 15px;font-family: inherit;">7.5</div>',
			angle: 0,
			zIndex: '1',
			radius: '-10%',
		}
	];

	/** tooltip */
	public tooltip: Object = {
		enable: true,
		template: '<div style="font-size:18px;background:white;width:180px;color:#595959;border:1px solid #e8e8e8">Current Score: 7.5 </div>'
	};

	/** majorTicks */
	public majorTicks: Object = {
		height: 5,
		width: 1.5,
		interval: 1.0,
		offset: 0
	};

	/** minorTicks */
	public minorTicks: Object = {
		height: 5,
		width: 0.5,
		interval: 0.5,
		offset: 0
	};

	/** isMobileView */
	public isMobileView: boolean;

	/** resizeObserver */
	resizeObserver!: ResizeObserver;

	/** healthDiv */
	@ViewChild('healthDiv') healthDiv!: ElementRef;

	constructor(
		public assetsDataService: AssetsDataService,
		public authenticationService: AuthenticationService,
		public organisationDataService: OrganisationDataService,
		private router: Router,
		public siteSubscriptions: siteSubscriptions,
		private route: ActivatedRoute,
		private globalFunctions: GlobalFunctions,
		private datapointsDataService: DatapointsDataService,
		private rulesDataService: RulesDataService,
		private integrationService: IntegrationsService
	) {
		this.globalFunctions.displaySpinner(true)
		combineLatest([
			this.siteSubscriptions.selectedAccountGUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.siteSubscriptions.selectedOrganisationUID$.pipe(takeUntil(this.finalise), distinctUntilChanged()),
			this.route.queryParamMap.pipe(takeUntil(this.finalise), distinctUntilChanged())
		]).pipe(
			// Ensure that subscriptions are not null or undefined
			filter(([selectedAccountGUID, selectedOrganisationUID, queryParamMap]) => !!selectedAccountGUID && !!queryParamMap && !!selectedOrganisationUID)
		).subscribe({
			next: ([selectedAccountGUID, selectedOrganisationUID, queryParamMap]) => {
				this.accountID = selectedAccountGUID;
				this.OrganisationUID = selectedOrganisationUID;

				this.getHealthData();
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * Page after view init Event Handler
	 */
	ngAfterViewInit() {
		this.setupResizeObserver();
	}

	/** getHealthData */
	getHealthData() {
		this.getOfflineDataPoints();
		this.getRules();
		this.getWebhooks();
	}

	/** getOfflineDataPoints */
	getOfflineDataPoints() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.datapointsDataService.getOfflineDataPoints$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (datapoints: any) => {
				if (datapoints) {
					this.HealthValue--;
				}
				const filteredDatapoints = datapoints.filter((dp => {
					const seenSensorIDs = new Set<number>();
					return (datapoint: DataPoint) => {
						if (!seenSensorIDs.has(datapoint.SensorID)) {
							seenSensorIDs.add(datapoint.SensorID);
							return true;
						}
						return false;
					};
				})());
				this.Datapoints = filteredDatapoints;
			},
			error: (error) => {
				if (error.status === 404) {

				}
			}
		});
	}

	/** getRules */
	getRules() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.rulesDataService.getRules$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (Rules: any) => {
				this.Rules = Rules.filter((x: Rule) => x.Active == true);
				if (this.Rules.length > 0) {
					this.HealthValue--;
				}
			},
			error: (error) => {
				if (error.status === 404) {

				}
			}
		});
	}

	/** getWebhooks */
	getWebhooks() {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.integrationService.getWebhookIntegrations$(this.accountID, this.OrganisationUID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((assets) => !!assets),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
				this.globalFunctions.displaySpinner(false);
			})
		).subscribe({
			next: (Webhooks: any) => {
				this.Webhooks = Webhooks.filter((x: IWebhookIntegration) => (x.Offline == true && x.Enabled == true));
				if (this.Webhooks.length > 0) {
					this.HealthValue--;
				}
			},
			error: (error) => {
				if (error.status === 404) {

				}
			}
		});
	}

	/** load */
	load(args: ILoadedEventArgs): void {
		// custom code start

		let selectedTheme: string = location.hash.split('/')[1];
		selectedTheme = selectedTheme ? selectedTheme : 'Material';
		args.gauge.theme = <GaugeTheme>(selectedTheme.charAt(0).toUpperCase() +
			selectedTheme.slice(1)).replace(/-dark/i, 'Dark').replace(/contrast/i, 'Contrast').replace(/-high/i, 'High').replace(/5.3/i, '5');
		// custom code end
	}

	/** setupResizeObserver */
	setupResizeObserver() {
		// Create a new ResizeObserver to listen for size changes of the parent div
		this.resizeObserver = new ResizeObserver((entries) => {
			for (let entry of entries) {
				const parentDivWidth = entry.contentRect.width;  // Get the parent's width
				const windowWidth = window.innerWidth;

				// Set isMobileView based on window width or parent div size
				this.isMobileView = (windowWidth < 1000 && windowWidth > 800) || (parentDivWidth < 1000 && parentDivWidth > 800);
			}
		});

		// Observe the infoDiv element
		if (this.healthDiv) {
			this.resizeObserver.observe(this.healthDiv.nativeElement);
		}
	}
}
